import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';

/**
 * Error types for better error categorization
 */
export enum ErrorType {
  NETWORK = 'NETWORK',
  SERVER = 'SERVER',
  CLIENT = 'CLIENT',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  VALIDATION = 'VALIDATION',
  NOT_FOUND = 'NOT_FOUND',
  PAYMENT = 'PAYMENT',
  CAPACITY = 'CAPACITY',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Structured error interface
 */
export interface AppError {
  type: ErrorType;
  message: string;
  statusCode?: number;
  originalError?: any;
  timestamp: Date;
}

/**
 * Centralized error handling service
 * Provides consistent error extraction, logging, and categorization
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  /**
   * Extract user-friendly error message from HTTP error
   * @param error HTTP error response
   * @returns User-friendly error message
   */
  extractErrorMessage(error: HttpErrorResponse | any): string {
    // Handle HTTP errors
    if (error instanceof HttpErrorResponse) {
      // Check for error.error.detail (common API pattern)
      if (error.error?.detail) {
        const detail = error.error.detail;
        // Handle structured error objects (e.g. capacity 409 responses)
        if (typeof detail === 'object' && detail !== null) {
          if (detail.message === 'Insufficient server capacity') {
            let msg = `Not enough server capacity. You need ${detail.required_ram_mb} MB RAM but only ${detail.available_ram_mb} MB is available.`;
            if (detail.upgrade_options?.length) {
              const cheapest = detail.upgrade_options[0];
              msg += ` Consider upgrading to ${cheapest.description} (\u20AC${cheapest.price}/mo).`;
            }
            return msg;
          }
          return detail.message || JSON.stringify(detail);
        }
        return detail;
      }

      // Check for error.error.message
      if (error.error?.message) {
        return error.error.message;
      }

      // Status-based messages
      switch (error.status) {
        case 0:
          return 'Unable to connect to server. Please check your connection.';
        case 400:
          return 'Invalid request. Please check your input.';
        case 401:
          return 'Authentication required. Please log in.';
        case 403:
          return 'You do not have permission to access this resource.';
        case 404:
          return 'The requested resource was not found.';
        case 402:
          return 'No payment method available.';
        case 500:
          return 'Internal server error. Please try again later.';
        case 503:
          return 'Service temporarily unavailable. Please try again later.';
        default:
          return `An error occurred (${error.status}). Please try again.`;
      }
    }

    // Handle non-HTTP errors
    if (error?.message) {
      return error.message;
    }

    // Fallback
    return 'An unexpected error occurred. Please try again.';
  }

  /**
   * Categorize error type based on HTTP error
   * @param error HTTP error response
   * @returns Error type
   */
  categorizeError(error: HttpErrorResponse | any): ErrorType {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 0:
          return ErrorType.NETWORK;
        case 400:
          return ErrorType.VALIDATION;
        case 401:
          return ErrorType.AUTHENTICATION;
        case 403:
          return ErrorType.AUTHORIZATION;
        case 404:
          return ErrorType.NOT_FOUND;
        case 402:
          return ErrorType.PAYMENT;
        case 409:
          if (error.error?.detail?.message === 'Insufficient server capacity') {
            return ErrorType.CAPACITY;
          }
          if (error.error?.detail?.state === 'onboarding_required') {
            return ErrorType.AUTHENTICATION;
          }
          return ErrorType.CLIENT;
        case 500:
        case 502:
        case 503:
        case 504:
          return ErrorType.SERVER;
        default:
          if (error.status >= 400 && error.status < 500) {
            return ErrorType.CLIENT;
          }
          return ErrorType.SERVER;
      }
    }

    return ErrorType.UNKNOWN;
  }

  /**
   * Create a structured AppError from any error
   * @param error Any error object
   * @returns Structured AppError
   */
  createAppError(error: HttpErrorResponse | any): AppError {
    const message = this.extractErrorMessage(error);
    const type = this.categorizeError(error);
    const statusCode = error instanceof HttpErrorResponse ? error.status : undefined;

    return {
      type,
      message,
      statusCode,
      originalError: error,
      timestamp: new Date(),
    };
  }

  /**
   * Handle HTTP error and return observable error
   * @param error HTTP error response
   * @param context Optional context for logging
   * @returns Observable error
   */
  handleError(error: HttpErrorResponse | any, context?: string): Observable<never> {
    const appError = this.createAppError(error);

    // Log error (in production, this could send to Sentry)
    this.logError(appError, context);

    // Return observable error with user-friendly message
    return throwError(() => appError);
  }

  /**
   * Log error to console or external service
   * @param error AppError object
   * @param context Optional context string
   */
  private logError(error: AppError, context?: string): void {
    const logMessage = context ? `[${context}] ${error.message}` : error.message;

    // In production, this could be sent to Sentry or other logging service
    console.error(logMessage, {
      type: error.type,
      statusCode: error.statusCode,
      timestamp: error.timestamp,
      originalError: error.originalError,
    });
  }

  /**
   * Check if error is a payment-related error
   * @param error Any error object
   * @returns True if payment error
   */
  isPaymentError(error: HttpErrorResponse | any): boolean {
    if (error instanceof HttpErrorResponse && error.status === 402) {
      return true;
    }

    const message = this.extractErrorMessage(error);
    return message.toLowerCase().includes('payment');
  }

  /**
   * Check if error is an authentication error
   * @param error Any error object
   * @returns True if authentication error
   */
  isAuthError(error: HttpErrorResponse | any): boolean {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      return true;
    }

    return false;
  }

  /**
   * Check if error is a network error
   * @param error Any error object
   * @returns True if network error
   */
  isNetworkError(error: HttpErrorResponse | any): boolean {
    if (error instanceof HttpErrorResponse && error.status === 0) {
      return true;
    }

    return false;
  }

  isCapacityError(error: HttpErrorResponse | any): boolean {
    return this.categorizeError(error) === ErrorType.CAPACITY;
  }
}
